import type { LoginType } from '@eureka/air/src/domains/shared';
import { InitCookie } from '@patsnap/init-cookie';
import { createApp, h } from 'vue';
import type { App } from 'vue';
import type { I18n } from 'vue-i18n';
import { getGlobalNamespace, interceptAxios } from '@patsnap-utils/passport';
import { setPostByFormPassport, psAxios } from '@patsnap/common-ps-http';
import type Passport from '@patsnap-utils/passport/src/passport';
import GoogleSignInPlugin from 'vue3-google-signin'
import { cn, en, jp, tw } from './src/locales'
import { getCurrentLanguage } from '@eureka/language-tool'
import type { InitFormParams } from './src/login-us/types/accounts';
import _ from 'lodash';
import { handleThirdPartyLoginErrors } from './src/login-us/utils/third-login-error-handler';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';

export interface OpenCallbackParams {
    token: string
    refresh_token: string
    passport: Passport
}

export enum DialogMode {
    LOGIN = 'login',
    REGISTER = 'register'
}

export enum WUJIE_PROPS_USER_ENTRY_METHOD {
    /**
     * 微信登录注册
     */
    Wechat = 'wechat',
    /**
     * 手机号登录注册
     */
    SMS = 'sms',
    /**
     * 账号密码登录
     */
    Account = 'account',
    /**
     * sso carsi/saml登录注册
     */
    SSO = 'sso',
    /**
     * google登录
     */
    Google = 'google',
    /**
     * 免费注册
     */
    FreeRegister = 'freeRegister'
}

export interface OpenFallbackParams {
    close?: boolean
    error?: Error
}

export enum SDK_ENV {
    CN = 'CN',
    US = 'US',
    EU = 'EU'
}

export interface LoginSDKOptions {
    i18n: I18n
    env: SDK_ENV
    channels: Channels
    autoPreload?: boolean // 是否在setI18n后自动预加载，默认true
}

export interface OpenOptions {
    withoutClose?: boolean
    initFormParams?: InitFormParams
    isSignup?: boolean
    silence?: boolean
    loginType?: LoginType
}

export type ThirdPartyLoginMethod = 'google' | 'microsoft' | 'linkedin'

export interface Channels {
    from?: string
    from_page?: string
    channel_source?: string
    activity_type?: string
    inviter_id?: string
    invitation_code?: string
    anonymousLogin?: boolean
}

export enum EMPOWER_TYPE {
    AIR_HOME = 'AIR_HOME',
    FMEA = 'FMEA',
    MONITOR = 'MONITOR',
    TECH_RESEARCH = 'TECH_RESEARCH',
    TECH_MIND = 'TECH_MIND',
    MATERIAL_MIND = 'MATERIAL_MIND',
    HISTORY = 'HISTORY'
}

export enum CHANNEL_SOURCE {
    AIR_EUREKA = 'dialog_signup_eureka'
}

function getRegistrationSource(): string {
    const pathname = window.location.pathname;
    const routeName = pathname.split('/').filter(Boolean)[0] || 'home';
    if (routeName === 'home') {
        return 'eureka_home';
    }
    if (routeName === 'ai-search') {
        return 'comon_tech_qa';
    }
    return routeName;
}

function trackSignUpSuccess(data: { new_user: boolean }) {
    if (data.new_user) {
        // 确保已安装 gtm packages/shared/gtm/index.ts
        console.log('sign_up_success ')
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'sign_up_success',
        });
    }
}

export class LoginSDK {
    private i18n: I18n | undefined = undefined;
    private env: SDK_ENV = SDK_ENV.CN
    private channels: Channels = {};
    private _div: HTMLDivElement | null = null;
    private _html: HTMLHtmlElement | null = null;
    private _app: App<Element> | null = null;
    private _isPreloaded: boolean = false;
    private _isPreloading: boolean = false;
    private _preloadPromise: Promise<void> | null = null;
    private _autoPreload: boolean = true;

    constructor(options?: LoginSDKOptions | undefined) {
        if (options?.i18n) {
            this.i18n = options.i18n
        }
        if (options?.env) {
            this.env = options.env
        }
        if (options?.channels) {
            this.channels = options.channels
        }
        if (options?.autoPreload !== undefined) {
            this._autoPreload = options.autoPreload
        }
        this.pollifyRic();
        // 如果已经有i18n和env，立即预加载
        if (this.i18n && this.env && this._autoPreload) {
            this.preload();
        }

        return this
    }

    private checkI18n() {
        if (!this.i18n) {
            throw new Error('i18n is required')
        }
    }

    getI18n() {
        return this.i18n
    }

    setI18n(i18n: I18n) {
        this.i18n = i18n
        const locale = getCurrentLanguage()
        const messages = {
            cn,
            en,
            jp,
            tw,
        }
        this.i18n.global.mergeLocaleMessage(locale, _.get(messages, locale))
        return this
    }

    setEnv(env: SDK_ENV) {
        this.env = env
        return this
    }

    setChannels(channels: Channels) {
        this.channels = channels
        return this
    }

    pollifyRic() {
        if (!window.requestIdleCallback) {
            window.requestIdleCallback = function(callback, options?: IdleRequestOptions | undefined) {
                const { timeout = 0 } = options || {};
                const startTime = Date.now();

                return setTimeout(function() {
                    // eslint-disable-next-line node/no-callback-literal
                    callback({
                        timeRemaining: function() {
                            return Math.max(0, timeout - (Date.now() - startTime));
                        },
                        didTimeout: Boolean(timeout) && Date.now() - startTime >= timeout,
                    });
                }, 1) as unknown as number;
            };
        }

        if (!window.cancelIdleCallback) {
            window.cancelIdleCallback = function(id) {
                clearTimeout(id);
            };
        }
    }

    async getLoginUI() {
        return this.env === SDK_ENV.CN
            ? await import('./src/LoginCN.vue').then(m => m.default)
            : await import('./src/LoginUSNext.vue').then(m => m.default)
    }

    /**
     * 预加载登录组件和wujie资源
     * 提前创建Vue实例并mount到隐藏容器，触发wujie远程资源加载
     */
    async _preload() {
        if (this._isPreloaded || this._isPreloading) {
            return this._preloadPromise;
        }

        this.checkI18n();
        this._isPreloading = true;

        this._preloadPromise = (async() => {
            try {
                // 创建隐藏容器
                this._div = document.createElement('div');
                this._div.setAttribute('data-theme', 'patsnapRD');
                this._div.style.cssText = 'display:none;position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;';
                document.body.appendChild(this._div);

                // 加载组件
                const component = await this.getLoginUI();
                const isSignup = !localStorage.getItem('_PATSNAP_ACCOUNT');

                // 创建Vue实例并mount（触发wujie加载）
                const $vm = this;
                this._app = createApp({
                    render() {
                        return h(component, {
                            isSignup,
                            channels: {
                                from: 'eureka',
                                anonymousLogin: false,
                                ...$vm.channels,
                            },
                            onClickRegister: (method: string) => {
                                const biTrackService = injectService<BiTrackService>(BiTrackService);
                                biTrackService.biTrack(EurekaTrackEventType.ss_plg_register, {
                                    sourceType: 'register_click',
                                    eventDetailStr: {
                                        signatureId: InitCookie.getVisitorId(),
                                        campaignName: InitCookie.getCampaignName(),
                                        registrationSource: getRegistrationSource(),
                                        registrationMethod: method,
                                    },
                                });
                            },
                            onSuccess: async(data: {new_user: boolean}) => {
                                trackSignUpSuccess(data);
                                const { token, passport } = await $vm._prepareTokenBase()
                                $vm._resolveOpen?.({ ...data, token, passport });
                                $vm.close()
                            },
                            onRegisterSuccess: (data: { new_user: boolean }) => {
                                trackSignUpSuccess(data);
                            },
                            onFailed: (error: Error) => {
                                $vm._rejectOpen?.({ error });
                            },
                            onClose: () => {
                                try {
                                    const biTrackService = injectService<BiTrackService>(BiTrackService);
                                    biTrackService.biTrack(EurekaTrackEventType.ss_plg_register, {
                                        sourceType: 'register_close',
                                        eventDetailStr: {
                                            signatureId: InitCookie.getVisitorId(),
                                            campaignName: InitCookie.getCampaignName(),
                                            registrationSource: getRegistrationSource(),
                                        },
                                    });
                                } catch (e) {
                                    console.error('[LoginSDK] biTrack failed:', e);
                                }
                                $vm._rejectOpen?.({ close: true });
                                $vm.close()
                            },
                        });
                    },
                });

                if (this.env === SDK_ENV.US) {
                    this._registerGoogleLogin(this._app)
                }
                this._app.use(this.i18n!)
                this._app.mount(this._div);

                this._isPreloaded = true;
                this._isPreloading = false;
                console.debug('[LoginSDK] Preloaded and mounted, wujie resources loading');
            } catch (error) {
                this._isPreloading = false;
                console.error('[LoginSDK] Preload failed:', error);
                throw error;
            }
        })();

        return this._preloadPromise;
    }

    preload() {
        window.requestIdleCallback(() => {
            this._preload();
        });
    }

    private _resolveOpen?: (value: OpenCallbackParams) => void;
    private _rejectOpen?: (value: OpenFallbackParams) => void;

    private _showContainer(options?: OpenOptions) {
        if (!this._div) {
            throw new Error('[LoginSDK] Container not found, call preload() first');
        }

        this._html = window.parent.document.querySelector('html')
        this._html?.setAttribute('style', 'overflow:hidden')

        if (options?.silence) {
            this._div.style.cssText = 'display:flex;position:fixed;bottom:16px;right:16px;z-index:99999;border-radius:24px;box-shadow:0 8px 24px rgba(0,0,0,0.16);';
            this._html?.removeAttribute('style')
        } else {
            this._div.style.cssText = 'display:flex;align-items:center;justify-content:center;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;';
        }

        return [this._div, this._html];
    }

    private _createContainer() {
        if (this._div) {
            this._div.remove()
        }
        this._div = document.createElement('div');
        this._div.setAttribute('data-theme', 'patsnapRD');
        this._html = window.parent.document.querySelector('html')
        this._html?.setAttribute('style', 'overflow:hidden')
        this._div.style.cssText = 'display:flex;align-items:center;justify-content:center;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;';
        document.body.appendChild(this._div);
        return [this._div, this._html];
    }

    private _hideContainer() {
        if (this._div) {
            this._div.style.display = 'none';
        }
        this._html?.removeAttribute('style');
        return this;
    }

    private _destroyContainer() {
        this._div?.remove();
        this._app?.unmount();
        this._html?.removeAttribute('style');
        this._div = null;
        this._app = null;
        this._isPreloaded = false;
        return this;
    }

    private async _prepareTokenBase() {
        const { singletonPassport: passport } = getGlobalNamespace()
        const token = await passport.getAccessToken()
        if (!psAxios.psInterceptors.passportRequestInterceptor) {
            interceptAxios(psAxios);
        }
        setPostByFormPassport(passport)
        return {
            token,
            passport,
        }
    }

    private _registerGoogleLogin(app: App) {
        app.use(GoogleSignInPlugin, {
        // @ts-ignore
            clientId: import.meta.env.VITE_GOOGLE_LOGIN_CLIENT_ID,
        });
    }

    async open(options?: OpenOptions) {
        this.checkI18n();

        // 如果已预加载，但传入了 options，则销毁预加载实例，重新创建
        // 因为 wujie 在预加载模式下动态更新 props 会导致样式异常
        if (this._isPreloaded && options && Object.keys(options).length > 0) {
            console.debug('[LoginSDK] Options provided, destroying preloaded instance');
            this._destroyContainer();
        }

        // 如果已预加载且没有 options，直接显示
        if (this._isPreloaded && this._div && this._app) {
            // 如果已经有未完成的 Promise，先 reject 它
            if (this._rejectOpen) {
                this._rejectOpen({ close: true });
            }

            this._showContainer(options);

            const promise = new Promise((resolve: (value: OpenCallbackParams) => void, reject: (value: OpenFallbackParams) => void) => {
                this._resolveOpen = resolve;
                this._rejectOpen = reject;
            });
            return promise;
        }

        // 未预加载，走原来的逻辑
        const [div, html] = this._createContainer();
        if (options?.silence && html && div) {
            div.style.cssText = 'display:flex;position:fixed;bottom:16px;right:16px;z-index:99999;border-radius:24px;box-shadow:0 8px 24px rgba(0,0,0,0.16);';
            html.removeAttribute('style')
        }
        const component = await this.getLoginUI();
        const isSignup = options?.isSignup ?? !localStorage.getItem('_PATSNAP_ACCOUNT');
        try {
            const biTrackService = injectService<BiTrackService>(BiTrackService);
            biTrackService.biTrack(EurekaTrackEventType.ss_plg_register, {
                sourceType: 'register_exposure',
                eventDetailStr: {
                    signatureId: InitCookie.getVisitorId(),
                    campaignName: InitCookie.getCampaignName(),
                    registrationSource: getRegistrationSource(),
                },
            });
        } catch (e) {
            console.error('[LoginSDK] biTrack failed:', e);
        }

        const promise = new Promise((resolve: (value: OpenCallbackParams) => void, reject: (value: OpenFallbackParams) => void) => {
            const $vm = this;
            this._app = createApp({
                render() {
                    return h(component, {
                        ...options,
                        isSignup,
                        channels: {
                            from: 'eureka',
                            anonymousLogin: false,
                            ...$vm.channels,
                        },
                        onClickRegister: (method: string) => {
                            const biTrackService = injectService<BiTrackService>(BiTrackService);
                            biTrackService.biTrack(EurekaTrackEventType.ss_plg_register, {
                                sourceType: 'register_click',
                                eventDetailStr: {
                                    signatureId: InitCookie.getVisitorId(),
                                    campaignName: InitCookie.getCampaignName(),
                                    registrationSource: getRegistrationSource(),
                                    registrationMethod: method,
                                },
                            });
                        },
                        onSuccess: async(data: {new_user:boolean}) => {
                            trackSignUpSuccess(data);
                            const { token, passport } = await $vm._prepareTokenBase()
                            resolve({ ...data, token, passport });
                            $vm.close()
                        },
                        onFailed: (error: Error) => {
                            // eslint-disable-next-line
                            reject({ error });
                        },
                        onClose: () => {
                            try {
                                const biTrackService = injectService<BiTrackService>(BiTrackService);
                                biTrackService.biTrack(EurekaTrackEventType.ss_plg_register, {
                                    sourceType: 'register_close',
                                    eventDetailStr: {
                                        signatureId: InitCookie.getVisitorId(),
                                        campaignName: InitCookie.getCampaignName(),
                                        registrationSource: getRegistrationSource(),
                                    },
                                });
                            } catch (e) {
                                console.error('[LoginSDK] biTrack failed:', e);
                            }
                            // eslint-disable-next-line
                            reject({ close: true });
                            $vm.close()
                        },
                    });
                },
            });
            if (this.env === SDK_ENV.US) {
                this._registerGoogleLogin(this._app)
            }
            this._app.use(this.i18n!)
            this._app.mount(div);
        });
        return promise;
    }

    /**
     * 处理第三方登录错误(linkedin, microsoft)
     */
    handleThirdPartyLoginErrors() {
        if (this.env === SDK_ENV.CN) return;
        handleThirdPartyLoginErrors(this.i18n);
    }

    close() {
        // 如果有未完成的 Promise，先 reject 它
        if (this._rejectOpen) {
            this._rejectOpen({ close: true });
            this._resolveOpen = undefined;
            this._rejectOpen = undefined;
        }

        // 如果是预加载模式，只隐藏不销毁
        if (this._isPreloaded) {
            this._hideContainer();
        } else {
            this._destroyContainer();
        }
    }
}

export const loginSDK = new LoginSDK();

export function buildQueryChannels(queryObj: Channels | undefined) {
    if (!queryObj) return ''
    const results: string[] = []
    for (const key in queryObj) {
        const value = queryObj[key as keyof Channels]
        if (value !== undefined) {
            results.push(`${key}=${value}`)
        }
    }
    return results.join('&')
}
